import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type {
  Control,
  GapAnswer,
  StakeholderRole,
  TrustFamily,
} from '@wedpo/soc2';
import {
  DEMO_COMPANY,
  STAKEHOLDER_ORDER,
  fillCompany,
  roleShortLabels,
  roleTaxonomy,
  trustFamilyLabels,
} from '@wedpo/soc2';
import { OwnerBadge, FamilyChips } from '@/components/common';
import type { ViewId } from '@/lib/store';

/** The five plain-language answers. The first four map to a control status;
 *  `unsure` flags the control for help without scoring it. */
const CHOICES: { value: GapAnswer; label: string; hint: string }[] = [
  { value: 'met', label: 'Yes, fully', hint: 'In place and working' },
  { value: 'partial', label: 'Partly', hint: 'Started or informal' },
  { value: 'gap', label: 'Not yet', hint: 'Nothing in place' },
  { value: 'na', label: "Doesn't apply", hint: 'Out of scope for us' },
  { value: 'unsure', label: "I don't understand", hint: 'Flag this for help' },
];

const GAP_LABEL: Record<string, string> = {
  partial: 'Partial',
  gap: 'Gap',
};

/** Traffic-light tone for a readiness score: red below 50, amber 50-79,
 *  green from 80. Used to color the readiness percentage. */
const readyTone = (p: number) =>
  p >= 80 ? 'tone-green' : p >= 50 ? 'tone-amber' : 'tone-red';

interface GapAnalysisProps {
  inScope: Control[];
  answers: Record<string, GapAnswer>;
  notes: Record<string, string>;
  evidence: Record<string, string[]>;
  rateControl: (code: string, answer: GapAnswer) => void;
  noteControl: (code: string, note: string) => void;
  attachEvidence: (code: string, fileNames: string[]) => void;
  removeEvidence: (code: string, fileName: string) => void;
  p2Done: boolean;
  go: (v: ViewId) => void;
}

export function GapAnalysis(props: GapAnalysisProps) {
  const { inScope, answers, notes, evidence, p2Done, go } = props;
  const [activeRole, setActiveRole] = useState<StakeholderRole | null>(null);
  // The combined gap report + remediation plan document, and whether it was
  // opened via "Submit" (which then offers a step through to Phase 3).
  const [docOpen, setDocOpen] = useState(false);
  const [submitMode, setSubmitMode] = useState(false);
  // Anchor for the role cards: when a visitor leaves a role's questionnaire we
  // scroll back here, not to the gap list at the very bottom of the page.
  const rolesRef = useRef<HTMLHeadingElement>(null);
  const [returnToRoles, setReturnToRoles] = useState(false);

  // A control counts as fully answered once it has a choice. "Doesn't apply"
  // is the one case that also needs a written reason, so it is only complete
  // when a note is present (see the N/A warning in the question card).
  const isComplete = (c: Control) => {
    const a = answers[c.code];
    if (!a) return false;
    if (a === 'na') return Boolean((notes[c.code] ?? '').trim());
    return true;
  };

  // In-scope controls grouped by owning role, in the canonical RACI order.
  const roleGroups = useMemo(() => {
    const map = new Map<StakeholderRole, Control[]>();
    for (const c of inScope) {
      const list = map.get(c.stakeholder) ?? [];
      list.push(c);
      map.set(c.stakeholder, list);
    }
    return STAKEHOLDER_ORDER.filter((r) => map.has(r)).map(
      (r) => [r, map.get(r)!] as const,
    );
  }, [inScope]);

  // Returning to the overview lands on the role cards. We run this after the
  // overview has re-rendered (activeRole back to null) so the ref exists.
  useEffect(() => {
    if (activeRole === null && returnToRoles) {
      // This effect runs after the overview has committed, so the heading is in
      // the DOM. Read its absolute offset (getBoundingClientRect forces layout)
      // and scroll there. We do it synchronously rather than in a rAF so the
      // jump still lands even when the tab is backgrounded.
      const el = rolesRef.current;
      if (el) {
        const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - 12);
        window.scrollTo(0, top);
      }
      setReturnToRoles(false);
    }
  }, [activeRole, returnToRoles]);

  const backToRoles = () => {
    setReturnToRoles(true);
    setActiveRole(null);
  };

  // Jump straight to another role and start at the top of its questionnaire.
  const openRole = (r: StakeholderRole) => {
    setActiveRole(r);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const total = inScope.length;
  const answered = inScope.filter((c) => isComplete(c)).length;
  const pct = total ? Math.round((answered / total) * 100) : 0;
  const count = (s: GapAnswer) =>
    inScope.filter((c) => answers[c.code] === s).length;

  // Live readiness: met = full, partial = half, over rated controls that are
  // neither N/A nor "unsure" (those are excluded from the score).
  const scored = inScope.filter(
    (c) => answers[c.code] && answers[c.code] !== 'na' && answers[c.code] !== 'unsure',
  );
  const readiness = scored.length
    ? Math.round(
        (scored.reduce(
          (a, c) =>
            a + (answers[c.code] === 'met' ? 1 : answers[c.code] === 'partial' ? 0.5 : 0),
          0,
        ) /
          scored.length) *
          100,
      )
    : 0;

  const activeIdx = activeRole
    ? roleGroups.findIndex(([r]) => r === activeRole)
    : -1;
  const active = activeIdx >= 0 ? roleGroups[activeIdx] : undefined;
  const nextRole =
    activeIdx >= 0 && activeIdx < roleGroups.length - 1
      ? roleGroups[activeIdx + 1][0]
      : null;

  // "Start full assessment" walks every role in order: it opens the first role
  // that still has unanswered controls (or the first role), and the per-role
  // "Next role →" chain carries the owner through the rest.
  const firstIncomplete = roleGroups.find(([, items]) =>
    items.some((c) => !isComplete(c)),
  );
  const fullStartTarget = (firstIncomplete ?? roleGroups[0])?.[0] ?? null;
  const fullStartLabel =
    answered === 0
      ? 'Start full assessment'
      : answered < total
        ? 'Resume full assessment'
        : 'Review all answers';

  return (
    <section>
      <p className="eyebrow">Phase 01 · Assess</p>

      {active ? (
        <RoleAssessment
          role={active[0]}
          items={active[1]}
          back={backToRoles}
          nextRole={nextRole}
          openRole={openRole}
          {...props}
        />
      ) : (
        <>
          <h1 className="title">Where do you stand today?</h1>
          <p className="sub">
            We have grouped your {total} in-scope controls by the team that owns them.
            Work through one role at a time, in plain language. We turn the gaps into
            your remediation plan in Phase 3, and you can change any answer later.
          </p>

          {fullStartTarget && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.85rem',
                flexWrap: 'wrap',
                margin: '0 0 1.25rem',
              }}
            >
              <button
                className="btn btn-grad"
                onClick={() => openRole(fullStartTarget)}
              >
                {fullStartLabel}
              </button>
              <span className="sub" style={{ margin: 0 }}>
                Walk every role in order, or start a single team below.
              </span>
            </div>
          )}

          <div className="gp-progress">
            <div className="gp-bar" aria-hidden>
              <span className="gp-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="gp-pmeta">
              <strong>{answered}</strong> of {total} answered · {pct}%
            </div>
          </div>

          <div className="grid4" style={{ marginBottom: '1.5rem' }}>
            <div className="kpi">
              <div className="n">{count('met')}</div>
              <div className="l">in place</div>
            </div>
            <div className="kpi">
              <div className="n">{count('partial')}</div>
              <div className="l">partial</div>
            </div>
            <div className="kpi">
              <div className="n">{count('gap')}</div>
              <div className="l">gaps</div>
            </div>
            <div className="kpi">
              <div className={`n ${readyTone(readiness)}`}>{readiness}%</div>
              <div className="l">readiness</div>
            </div>
          </div>

          {p2Done && (
            <div className="gp-done">
              <div className="gp-done-h">
                <span className="gp-check">✓</span>
                <strong>Every in-scope control is rated.</strong>
              </div>
              <p>
                Your assessment is complete. Submit to lock it in and move into Phase 3
                where each gap becomes a tracked task, or generate the report below to
                share with your team or auditor first.
              </p>
              <div className="gp-done-actions">
                <button
                  className="btn btn-dark"
                  onClick={() => {
                    setSubmitMode(true);
                    setDocOpen(true);
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          )}

          <h2 className="gp-roles-h" ref={rolesRef}>
            Assessment by role
          </h2>
          <div className="gp-roles">
            {roleGroups.map(([role, items]) => {
              const done = items.filter((c) => isComplete(c)).length;
              const gaps = items.filter(
                (c) => answers[c.code] === 'gap' || answers[c.code] === 'partial',
              ).length;
              const help = items.filter((c) => answers[c.code] === 'unsure').length;
              const needReason = items.filter(
                (c) => answers[c.code] === 'na' && !(notes[c.code] ?? '').trim(),
              ).length;
              const rpct = Math.round((done / items.length) * 100);
              const label =
                done === 0
                  ? 'Start assessment'
                  : done < items.length
                    ? 'Continue assessment'
                    : 'Review answers';
              return (
                <div className="gp-rolecard" key={role}>
                  <div className="gp-rolecard-top">
                    <OwnerBadge role={role} />
                    <span className="gp-rolecard-count">
                      {items.length} controls
                    </span>
                  </div>
                  <p className="gp-rolecard-desc">{roleTaxonomy[role]}</p>
                  <div className="gp-bar gp-bar-sm" aria-hidden>
                    <span className="gp-fill" style={{ width: `${rpct}%` }} />
                  </div>
                  <div className="gp-rolecard-meta">
                    <span>
                      {done}/{items.length} answered
                    </span>
                    {gaps > 0 && <span className="gp-tag gp-tag-gap">{gaps} to fix</span>}
                    {help > 0 && (
                      <span className="gp-tag gp-tag-help">{help} need help</span>
                    )}
                    {needReason > 0 && (
                      <span className="gp-tag gp-tag-warn">
                        {needReason} need a reason
                      </span>
                    )}
                  </div>
                  <button
                    className={`btn ${done === items.length ? 'btn-ghost' : 'btn-grad'} gp-rolecard-btn`}
                    onClick={() => setActiveRole(role)}
                  >
                    {label}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="gp-gen">
            <div className="gp-gen-txt">
              <strong>Gap analysis report and remediation plan</strong>
              <span>
                Generate a shareable document from your answers so far. You can
                regenerate it any time as you fill in more.
              </span>
            </div>
            <button
              className="btn btn-grad gp-gen-btn"
              onClick={() => {
                setSubmitMode(false);
                setDocOpen(true);
              }}
            >
              Generate gap analysis report and remediation plan
            </button>
          </div>

          <GapReport inScope={inScope} answers={answers} />
        </>
      )}

      {docOpen &&
        createPortal(
          <DocOverlay
            title="Gap analysis report & remediation plan"
            rows={[
              ...reportMatrix(inScope, answers, notes, evidence),
              [],
              ...planMatrix(inScope, answers, notes),
            ]}
            csvName={`wedpo-soc2-gap-report-and-remediation-plan-${fileStamp()}.csv`}
            onClose={() => {
              setDocOpen(false);
              setSubmitMode(false);
            }}
            onContinue={
              submitMode
                ? () => {
                    setDocOpen(false);
                    setSubmitMode(false);
                    go('p3');
                  }
                : undefined
            }
          >
            <ReportDoc
              inScope={inScope}
              answers={answers}
              notes={notes}
              evidence={evidence}
            />
            <div className="gp-doc-pagebreak" />
            <PlanDoc inScope={inScope} answers={answers} notes={notes} />
          </DocOverlay>,
          document.body,
        )}
    </section>
  );
}

/* ---------- one role's questions ---------- */

function RoleAssessment({
  role,
  items,
  back,
  nextRole,
  openRole,
  answers,
  notes,
  evidence,
  rateControl,
  noteControl,
  attachEvidence,
  removeEvidence,
}: {
  role: StakeholderRole;
  items: Control[];
  back: () => void;
  nextRole: StakeholderRole | null;
  openRole: (r: StakeholderRole) => void;
} & GapAnalysisProps) {
  const done = items.filter((c) => answers[c.code]).length;
  const pct = Math.round((done / items.length) * 100);

  return (
    <>
      <button className="gp-back" onClick={back}>
        ← All roles
      </button>
      <div className="gp-role-head">
        <h1 className="title" style={{ marginBottom: '0.35rem' }}>
          {roleShortLabels[role]} assessment
        </h1>
        <OwnerBadge role={role} />
      </div>
      <p className="sub">{roleTaxonomy[role]}</p>

      <div className="gp-progress">
        <div className="gp-bar" aria-hidden>
          <span className="gp-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="gp-pmeta">
          <strong>{done}</strong> of {items.length} answered in this role · {pct}%
        </div>
      </div>

      {items.map((c) => (
        <QuestionCard
          key={c.code}
          control={c}
          answer={answers[c.code]}
          note={notes[c.code] ?? ''}
          evidenceFiles={evidence[c.code] ?? []}
          onRate={(a) => rateControl(c.code, a)}
          onNote={(n) => noteControl(c.code, n)}
          onAttach={(names) => attachEvidence(c.code, names)}
          onRemove={(name) => removeEvidence(c.code, name)}
        />
      ))}

      <div className="gp-role-foot">
        <button className="btn btn-ghost gp-back-btm" onClick={back}>
          ← Back to all roles
        </button>
        {nextRole ? (
          <button
            className="btn btn-grad gp-next-role"
            onClick={() => openRole(nextRole)}
          >
            Next role: {roleShortLabels[nextRole]} →
          </button>
        ) : (
          <button className="btn btn-grad gp-next-role" onClick={back}>
            Complete ✓
          </button>
        )}
      </div>
    </>
  );
}

function QuestionCard({
  control,
  answer,
  note,
  evidenceFiles,
  onRate,
  onNote,
  onAttach,
  onRemove,
}: {
  control: Control;
  answer?: GapAnswer;
  note: string;
  evidenceFiles: string[];
  onRate: (a: GapAnswer) => void;
  onNote: (n: string) => void;
  onAttach: (fileNames: string[]) => void;
  onRemove: (fileName: string) => void;
}) {
  return (
    <div className={`gp-card${answer ? ' answered' : ''}`}>
      <div className="gp-card-top">
        <div className="gp-q">
          <div className="gp-qcode">{control.code}</div>
          <div className="gp-qtext">
            {fillCompany(control.question ?? control.name)}
            {control.example && (
              <span className="gp-tip" tabIndex={0} aria-label="See an example answer">
                <span className="gp-tip-icon">i</span>
                <span className="gp-tip-pop">
                  <span className="gp-tip-h">Example answer</span>
                  {fillCompany(control.example)}
                </span>
              </span>
            )}
          </div>
          {control.helper && (
            <div className="gp-qhelper">{fillCompany(control.helper)}</div>
          )}
        </div>
        <div className="gp-qmeta">
          <div className="gp-qfams">
            <FamilyChips families={control.trustFamilies} />
          </div>
        </div>
      </div>

      <div className="gp-choices gp-choices-5">
        {CHOICES.map((ch) => (
          <button
            key={ch.value}
            className={`gp-choice c-${ch.value}${answer === ch.value ? ' on' : ''}`}
            onClick={() => onRate(ch.value)}
          >
            <span className="gp-choice-l">{ch.label}</span>
            <span className="gp-choice-h">{ch.hint}</span>
          </button>
        ))}
      </div>

      <div className="gp-card-foot">
        {(() => {
          const isNa = answer === 'na';
          const naMissing = isNa && !note.trim();
          return (
            <label className={`gp-foot-label${naMissing ? ' gp-note-required' : ''}`}>
              {isNa ? (
                <span className="gp-note-warn-row">
                  <span className="gp-warn-ic" aria-hidden>
                    !
                  </span>
                  Tell us why this does not apply: a reasonable explanation is
                  required when you mark a control as not applicable.
                </span>
              ) : (
                'Gap note'
              )}
              <textarea
                className={`gp-note${naMissing ? ' gp-note-warn' : ''}`}
                placeholder={
                  isNa
                    ? 'e.g. We have no physical offices, so badge access controls are out of scope.'
                    : 'Context, owner, or what is missing (optional)'
                }
                value={note}
                onChange={(e) => onNote(e.target.value)}
              />
            </label>
          );
        })()}
        <div className="gp-erow">
          <span className="gp-foot-label gp-foot-label-inline gp-elabel">
            Evidence
            <span
              className="gp-tip gp-tip-end"
              tabIndex={0}
              aria-label="What makes good evidence"
            >
              <span className="gp-tip-icon">i</span>
              <span className="gp-tip-pop">
                <span className="gp-tip-h">What to attach</span>
                Attach dated proof the control is working: a screenshot, export, or
                policy page. Include the capture date, the system it came from (Okta,
                AWS, Jira...), and a clear file name like okta-mfa-2026-05.png. You can
                add several files.
              </span>
            </span>
          </span>
          {evidenceFiles.length > 0 && (
            <div className="gp-efiles">
              {evidenceFiles.map((name) => (
                <span className="gp-echip" key={name}>
                  <span className="gp-eclip">DOC</span>
                  <span className="gp-ename">{name}</span>
                  <button
                    type="button"
                    className="gp-ex"
                    onClick={() => onRemove(name)}
                    aria-label={`Remove ${name}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <label className="gp-ebtn">
            {evidenceFiles.length ? '+ Add more files' : '+ Upload files'}
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => {
                const names = Array.from(e.target.files ?? []).map((f) => f.name);
                if (names.length) onAttach(names);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

/* ---------- overall gap report ---------- */

function GapReport({
  inScope,
  answers,
}: {
  inScope: Control[];
  answers: Record<string, GapAnswer>;
}) {
  const byOwner = useMemo(() => {
    const map = new Map<StakeholderRole, Control[]>();
    for (const c of inScope) {
      if (answers[c.code] === 'gap' || answers[c.code] === 'partial') {
        const list = map.get(c.stakeholder) ?? [];
        list.push(c);
        map.set(c.stakeholder, list);
      }
    }
    return STAKEHOLDER_ORDER.filter((r) => map.has(r)).map(
      (r) => [r, map.get(r)!] as const,
    );
  }, [inScope, answers]);

  if (byOwner.length === 0) return null;

  return (
    <div className="gp-report">
      <h2 className="gp-report-h">Gap report</h2>
      <p className="sub" style={{ marginTop: 0 }}>
        Everything you flagged as a gap or partial, grouped by the team that owns it.
        This is what feeds your Phase 3 remediation plan.
      </p>
      {byOwner.map(([role, items]) => (
        <div className="gp-owner" key={role}>
          <div className="gp-owner-h">
            <OwnerBadge role={role} />
            <span className="gp-owner-n">
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </span>
          </div>
          <ul className="gp-owner-list">
            {items.map((c) => (
              <li key={c.code}>
                <span className={`status s-${answers[c.code]}`}>
                  {GAP_LABEL[answers[c.code] as string]}
                </span>
                <span className="gp-owner-code">{c.code}</span>
                <span className="gp-owner-name">{c.name}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* ---------- generated documents (report + plan) ---------- */

const STATUS_DOC_LABEL: Record<GapAnswer, string> = {
  met: 'In place',
  partial: 'Partial',
  gap: 'Gap',
  na: "Doesn't apply",
  unsure: 'Needs review',
};

/** Maps a questionnaire answer to its doc pill class (st-met, st-gap, ...). */
const stCls = (a?: GapAnswer) => (a ? `st st-${a}` : 'st st-na');

/** Trust families in a fixed display order. */
const FAMILY_ORDER: TrustFamily[] = ['CC', 'A', 'C', 'P', 'PI'];

/** Group controls by owning role, in the canonical RACI order. */
function ownerGroups(items: Control[]): [StakeholderRole, Control[]][] {
  const map = new Map<StakeholderRole, Control[]>();
  for (const c of items) {
    const list = map.get(c.stakeholder) ?? [];
    list.push(c);
    map.set(c.stakeholder, list);
  }
  return STAKEHOLDER_ORDER.filter((r) => map.has(r)).map(
    (r) => [r, map.get(r)!] as [StakeholderRole, Control[]],
  );
}

function docDate() {
  return new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** yyyy-mm-dd stamp for export file names. */
function fileStamp() {
  return new Date().toISOString().slice(0, 10);
}

/* ---------- tabular export (CSV / Google Sheets) ----------
 * Both the gap report and the plan flatten to a row matrix that mirrors the
 * register format from our procedure templates (title block, then a header
 * row, then one row per control). CSV downloads as a file; "Open in Google
 * Sheets" copies the same grid as TSV and opens a blank sheet to paste into,
 * since the app has no backend to push a real spreadsheet to. */

function csvCell(v: string) {
  const s = String(v ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows: string[][]) {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
}

function toTsv(rows: string[][]) {
  return rows
    .map((r) =>
      r.map((c) => String(c ?? '').replace(/\t/g, ' ').replace(/[\r\n]+/g, ' ')).join('\t'),
    )
    .join('\n');
}

function downloadCsv(name: string, rows: string[][]) {
  // Prepend a BOM so Excel and Sheets read UTF-8 correctly.
  const blob = new Blob(['﻿' + toCsv(rows)], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function openInSheets(rows: string[][]) {
  try {
    await navigator.clipboard.writeText(toTsv(rows));
    return true;
  } catch {
    return false;
  } finally {
    window.open('https://sheets.new', '_blank', 'noopener');
  }
}

function reportMatrix(
  inScope: Control[],
  answers: Record<string, GapAnswer>,
  notes: Record<string, string>,
  evidence: Record<string, string[]>,
): string[][] {
  const rows: string[][] = [
    ['WeDPO SOC 2 Readiness - Gap Analysis Report'],
    ['Company', DEMO_COMPANY],
    ['Generated', docDate()],
    [],
    ['Control', 'Name', 'Owner', 'Trust categories', 'Status', 'Note', 'Evidence'],
  ];
  for (const [role, items] of ownerGroups(inScope)) {
    for (const c of items) {
      rows.push([
        c.code,
        c.name,
        roleShortLabels[role],
        c.trustFamilies.map((f) => trustFamilyLabels[f]).join('; '),
        STATUS_DOC_LABEL[answers[c.code] ?? 'na'],
        (notes[c.code] ?? '').trim() || '-',
        (evidence[c.code] ?? []).join('; ') || '-',
      ]);
    }
  }
  return rows;
}

function planMatrix(
  inScope: Control[],
  answers: Record<string, GapAnswer>,
  notes: Record<string, string>,
): string[][] {
  const rows: string[][] = [
    ['WeDPO SOC 2 Readiness - Remediation Plan'],
    ['Company', DEMO_COMPANY],
    ['Generated', docDate()],
    [],
    ['#', 'Priority', 'Owner', 'Control', 'Name', 'Suggested action', 'Your note'],
  ];
  const open = inScope.filter(
    (c) => answers[c.code] === 'gap' || answers[c.code] === 'partial',
  );
  let i = 0;
  for (const [role, items] of ownerGroups(open)) {
    const sorted = [...items].sort(
      (a, b) =>
        (answers[a.code] === 'gap' ? 0 : 1) - (answers[b.code] === 'gap' ? 0 : 1),
    );
    for (const c of sorted) {
      const isGap = answers[c.code] === 'gap';
      rows.push([
        String(++i),
        isGap ? 'High' : 'Medium',
        roleShortLabels[role],
        c.code,
        c.name,
        (isGap
          ? 'Put this control in place. '
          : 'Formalize and finish what is started. ') + (c.helper ? fillCompany(c.helper) : ''),
        (notes[c.code] ?? '').trim() || '-',
      ]);
    }
  }
  return rows;
}

/** Full-screen, printable wrapper. The toolbar is hidden when printing; the
 *  overlay is portalled to <body> so print captures only the document page. */
function DocOverlay({
  title,
  rows,
  csvName,
  onClose,
  onContinue,
  children,
}: {
  title: string;
  rows: string[][];
  csvName: string;
  onClose: () => void;
  onContinue?: () => void;
  children: ReactNode;
}) {
  const [hint, setHint] = useState('');

  const toSheets = async () => {
    const copied = await openInSheets(rows);
    setHint(
      copied
        ? 'Copied. Paste into the new sheet with Cmd/Ctrl + V.'
        : 'A blank sheet opened. Export CSV and use File > Import.',
    );
    window.setTimeout(() => setHint(''), 6000);
  };

  return (
    <div className="gp-doc-overlay" role="dialog" aria-label={title}>
      <div className="gp-doc-bar">
        <span className="gp-doc-bar-t">{title}</span>
        {hint && <span className="gp-doc-bar-hint">{hint}</span>}
        <div className="gp-doc-bar-sp" />
        <button className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
        <button className="btn btn-ghost" onClick={() => downloadCsv(csvName, rows)}>
          Export CSV
        </button>
        <button className="btn btn-ghost" onClick={toSheets}>
          Google Sheets
        </button>
        <button
          className="btn btn-ghost"
          onClick={() =>
            window.open('https://app.wedpo.com', '_blank', 'noopener')
          }
        >
          Manage on we/dpo
        </button>
        <button
          className={onContinue ? 'btn btn-ghost' : 'btn btn-dark'}
          onClick={() => window.print()}
        >
          Download PDF
        </button>
        {onContinue && (
          <button className="btn btn-dark" onClick={onContinue}>
            Continue to Phase 3 →
          </button>
        )}
      </div>
      <div className="gp-doc-scroll">
        <div className="gp-doc">{children}</div>
      </div>
    </div>
  );
}

function DocHead({ kind }: { kind: string }) {
  return (
    <div className="gp-doc-head">
      <div className="gp-doc-brand">
        we<span>/</span>dpo
      </div>
      <p className="gp-doc-eyebrow">SOC 2 Readiness · Phase 2</p>
      <h1 className="gp-doc-title">{kind}</h1>
      <div className="gp-doc-meta">
        <span>
          Company: <strong>{DEMO_COMPANY}</strong>
        </span>
        <span>Generated {docDate()}</span>
      </div>
    </div>
  );
}

function ReportDoc({
  inScope,
  answers,
  notes,
  evidence,
}: {
  inScope: Control[];
  answers: Record<string, GapAnswer>;
  notes: Record<string, string>;
  evidence: Record<string, string[]>;
}) {
  const total = inScope.length;
  const count = (s: GapAnswer) => inScope.filter((c) => answers[c.code] === s).length;
  const scored = inScope.filter((c) => {
    const a = answers[c.code];
    return a && a !== 'na' && a !== 'unsure';
  });
  const readiness = scored.length
    ? Math.round(
        (scored.reduce(
          (a, c) =>
            a + (answers[c.code] === 'met' ? 1 : answers[c.code] === 'partial' ? 0.5 : 0),
          0,
        ) /
          scored.length) *
          100,
      )
    : 0;
  const fams = FAMILY_ORDER.filter((f) =>
    inScope.some((c) => c.trustFamilies.includes(f)),
  );
  const groups = ownerGroups(inScope);

  return (
    <>
      <DocHead kind="Gap Analysis Report" />

      <p className="gp-doc-intro">
        This report summarizes {total} in-scope controls across the {fams
          .map((f) => trustFamilyLabels[f])
          .join(', ')}{' '}
        {fams.length === 1 ? 'category' : 'categories'}, with the current status,
        owner, notes, and evidence recorded for each.
      </p>

      <div className="gp-doc-kpis">
        <div className="gp-doc-kpi">
          <div className="n">{count('met')}</div>
          <div className="l">In place</div>
        </div>
        <div className="gp-doc-kpi">
          <div className="n">{count('partial')}</div>
          <div className="l">Partial</div>
        </div>
        <div className="gp-doc-kpi">
          <div className="n">{count('gap')}</div>
          <div className="l">Gaps</div>
        </div>
        <div className="gp-doc-kpi">
          <div className="n">{count('na')}</div>
          <div className="l">N/A</div>
        </div>
        <div className="gp-doc-kpi">
          <div className="n">{count('unsure')}</div>
          <div className="l">Needs review</div>
        </div>
        <div className="gp-doc-kpi gp-doc-kpi-hi">
          <div className="n">{readiness}%</div>
          <div className="l">Readiness</div>
        </div>
      </div>

      {groups.map(([role, items]) => (
        <div className="gp-doc-sec" key={role}>
          <h2 className="gp-doc-sec-h">
            {roleShortLabels[role]}{' '}
            <span className="gp-doc-sec-n">{items.length} controls</span>
          </h2>
          <table className="gp-doc-table">
            <thead>
              <tr>
                <th style={{ width: '38%' }}>Control</th>
                <th style={{ width: '14%' }}>Status</th>
                <th>Note</th>
                <th style={{ width: '12%' }}>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const ev = (evidence[c.code] ?? []).length;
                const note = (notes[c.code] ?? '').trim();
                return (
                  <tr key={c.code}>
                    <td>
                      <span className="gp-doc-code">{c.code}</span>
                      {c.name}
                    </td>
                    <td>
                      <span className={stCls(answers[c.code])}>
                        {STATUS_DOC_LABEL[answers[c.code] ?? 'na']}
                      </span>
                    </td>
                    <td className="gp-doc-note">{note || '-'}</td>
                    <td>{ev ? `${ev} file${ev > 1 ? 's' : ''}` : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <p className="gp-doc-foot">
        WeDPO · SOC 2 readiness kit · This report reflects a self-assessment and is not
        an audit opinion.
      </p>
    </>
  );
}

function PlanDoc({
  inScope,
  answers,
  notes,
}: {
  inScope: Control[];
  answers: Record<string, GapAnswer>;
  notes: Record<string, string>;
}) {
  const open = inScope.filter(
    (c) => answers[c.code] === 'gap' || answers[c.code] === 'partial',
  );
  const help = inScope.filter((c) => answers[c.code] === 'unsure');
  const groups = ownerGroups(open).map(
    ([role, items]) =>
      [
        role,
        [...items].sort(
          (a, b) =>
            (answers[a.code] === 'gap' ? 0 : 1) - (answers[b.code] === 'gap' ? 0 : 1),
        ),
      ] as [StakeholderRole, Control[]],
  );
  const highs = open.filter((c) => answers[c.code] === 'gap').length;
  const meds = open.length - highs;

  return (
    <>
      <DocHead kind="Remediation Plan" />

      {open.length === 0 ? (
        <p className="gp-doc-empty">
          No open gaps. Every in-scope control is already in place or marked as not
          applicable. Move into Phase 3 to keep evidence collection on cadence.
        </p>
      ) : (
        <>
          <p className="gp-doc-intro">
            {open.length} action{open.length > 1 ? 's' : ''} across {groups.length}{' '}
            {groups.length === 1 ? 'owner' : 'owners'} ({highs} high, {meds} medium).
            Work the high-priority gaps first. Each action below maps to a control and
            becomes a tracked task in Phase 3.
          </p>

          {groups.map(([role, items]) => (
            <div className="gp-doc-sec" key={role}>
              <h2 className="gp-doc-sec-h">
                {roleShortLabels[role]}{' '}
                <span className="gp-doc-sec-n">
                  {items.length} action{items.length > 1 ? 's' : ''}
                </span>
              </h2>
              <table className="gp-doc-table">
                <thead>
                  <tr>
                    <th style={{ width: '7%' }}>#</th>
                    <th style={{ width: '13%' }}>Priority</th>
                    <th style={{ width: '32%' }}>Control</th>
                    <th>Suggested action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c, i) => {
                    const isGap = answers[c.code] === 'gap';
                    const note = (notes[c.code] ?? '').trim();
                    return (
                      <tr key={c.code}>
                        <td>{i + 1}</td>
                        <td>
                          <span className={isGap ? 'st st-gap' : 'st st-partial'}>
                            {isGap ? 'High' : 'Medium'}
                          </span>
                        </td>
                        <td>
                          <span className="gp-doc-code">{c.code}</span>
                          {c.name}
                        </td>
                        <td>
                          <div className="gp-doc-action">
                            {isGap ? 'Put this control in place.' : 'Formalize and finish what is started.'}{' '}
                            {c.helper ? fillCompany(c.helper) : ''}
                          </div>
                          {note && (
                            <div className="gp-doc-yournote">Your note: {note}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}

      {help.length > 0 && (
        <div className="gp-doc-sec">
          <h2 className="gp-doc-sec-h">
            Needs review with WeDPO{' '}
            <span className="gp-doc-sec-n">{help.length}</span>
          </h2>
          <p className="gp-doc-intro" style={{ marginTop: 0 }}>
            You flagged these as not yet understood. We will walk through them with you
            before they enter the plan.
          </p>
          <ul className="gp-doc-help">
            {help.map((c) => (
              <li key={c.code}>
                <span className="gp-doc-code">{c.code}</span>
                {c.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="gp-doc-foot">
        WeDPO · SOC 2 readiness kit · Priorities are a starting point: high = a missing
        control, medium = started but not complete.
      </p>
    </>
  );
}
